/**
 * <Accordion> — generic expand/collapse container (molecule, L0.1).
 *
 * A pressable header that toggles a content region. Works controlled (`expanded` +
 * `onToggle`) or uncontrolled (`defaultExpanded`, internal state).
 *
 * `header` may be a node, or a render function receiving the current `expanded` flag so
 * the header can flip its own affordance (e.g. a chevron). The content renders only when
 * expanded. This component owns no visual chrome beyond the pressable — wrap it in a card
 * shell where needed (see `<SchoolAssessmentAccordion>`).
 */
import React from "react"
import { Pressable, View, type StyleProp, type ViewStyle } from "react-native"

export type AccordionProps = {
    /** Header content, or a render function of the current expanded state. */
    header: React.ReactNode | ((expanded: boolean) => React.ReactNode)
    children: React.ReactNode
    /** Controlled open state. When provided, `onToggle` drives changes. */
    expanded?: boolean
    /** Called with the next state on header press (controlled mode). */
    onToggle?: (next: boolean) => void
    /** Initial open state in uncontrolled mode — defaults to `false`. */
    defaultExpanded?: boolean
    style?: StyleProp<ViewStyle>
    testID?: string
}

export function Accordion(props: AccordionProps) {
    const { header, children, expanded, onToggle, defaultExpanded = false, style, testID } = props

    const [internalOpen, setInternalOpen] = React.useState(defaultExpanded)
    const isControlled = expanded !== undefined
    const open = isControlled ? expanded : internalOpen

    const toggle = () => {
        const next = !open
        onToggle?.(next)
        if (!isControlled) setInternalOpen(next)
    }

    const headerNode = typeof header === "function" ? header(open) : header

    return (
        <View style={style} testID={testID}>
            <Pressable accessibilityRole="button" accessibilityState={{ expanded: open }} onPress={toggle}>
                {headerNode}
            </Pressable>
            {open && <View>{children}</View>}
        </View>
    )
}
